import React, { useState, useRef, useEffect, useMemo } from 'react';
import { X, Image, Trash2, Star, Upload, FolderOpen, Search, Check, Loader2 } from 'lucide-react';
import { uploadApi } from '../api/client';
import type { MediaItem } from '../api/modules/upload';

/**
 * Format bytes to human-readable string
 */
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

interface FaviconUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  siteId: string;
  currentFaviconUrl?: string;
  onFaviconUploaded: (faviconUrl: string) => void;
}

export const FaviconUploadModal: React.FC<FaviconUploadModalProps> = ({
  isOpen,
  onClose,
  siteId,
  currentFaviconUrl,
  onFaviconUploaded,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentFaviconUrl || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Tab state
  const [activeTab, setActiveTab] = useState<'upload' | 'library'>('upload');

  // Library state
  const [libraryMedia, setLibraryMedia] = useState<MediaItem[]>([]);
  const [isLoadingLibrary, setIsLoadingLibrary] = useState(false);
  const [libraryError, setLibraryError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLibraryMedia, setSelectedLibraryMedia] = useState<MediaItem | null>(null);

  // Filter library media based on search (images only for favicons)
  const filteredLibraryMedia = useMemo(() => {
    return libraryMedia.filter(item => {
      // Only show images suitable for favicons
      if (!item.mimeType.startsWith('image/')) return false;
      // Filter by search query
      if (searchQuery) {
        return item.filename.toLowerCase().includes(searchQuery.toLowerCase());
      }
      return true;
    });
  }, [libraryMedia, searchQuery]);

  // Fetch library media when switching to library tab
  useEffect(() => {
    if (activeTab === 'library' && isOpen) {
      const fetchLibraryMedia = async () => {
        setIsLoadingLibrary(true);
        setLibraryError(null);
        try {
          const media = await uploadApi.getMedia(siteId);
          setLibraryMedia(media);
        } catch (err) {
          console.error('Failed to fetch library media:', err);
          setLibraryError('Failed to load media library');
        } finally {
          setIsLoadingLibrary(false);
        }
      };
      fetchLibraryMedia();
    }
  }, [activeTab, isOpen, siteId]);

  // Reset preview when modal opens with new favicon
  useEffect(() => {
    if (isOpen) {
      setPreviewUrl(currentFaviconUrl || null);
      setError(null);
      setUploadProgress(0);
      setActiveTab('upload');
      setSelectedLibraryMedia(null);
      setSearchQuery('');
      setLibraryError(null);
    }
  }, [isOpen, currentFaviconUrl]);

  // Handle selecting from library
  const handleSelectFromLibrary = () => {
    if (selectedLibraryMedia) {
      const imageUrl = selectedLibraryMedia.cloudfrontUrl || selectedLibraryMedia.url;
      onFaviconUploaded(imageUrl);
      onClose();
    }
  };

  const handleFileSelect = (file: File) => {
    setError(null);

    // Validate file type - favicons can be ICO, PNG, JPG, SVG, or WebP
    const validTypes = [
      'image/x-icon',
      'image/vnd.microsoft.icon',
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/svg+xml',
      'image/webp',
    ];
    const isValidType = validTypes.includes(file.type) || file.name.endsWith('.ico');

    if (!isValidType) {
      setError('Please select a valid favicon file (ICO, PNG, JPG, SVG, or WebP)');
      return;
    }

    // Validate file size - favicons should be small (max 500KB)
    if (file.size > 500 * 1024) {
      setError('Favicon file size should be less than 500KB');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = e => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Upload file
    uploadFile(file);
  };

  const uploadFile = async (file: File) => {
    try {
      setIsUploading(true);
      setUploadProgress(0);
      setError(null);

      // Use presigned URL upload
      const response = await uploadApi.uploadWithPresignedUrl(file, {
        siteId,
        componentType: 'site',
        assetType: 'icon',
        onProgress: progress => {
          setUploadProgress(progress);
        },
      });

      setUploadProgress(100);

      // Use CloudFront URL if available, otherwise use S3 URL
      const faviconUrl = response.cloudfrontUrl || response.url;

      // Update the parent component with the new favicon URL
      onFaviconUploaded(faviconUrl);

      // Close modal after a brief delay to show completion
      setTimeout(() => {
        onClose();
        setUploadProgress(0);
      }, 500);
    } catch (err: any) {
      console.error('Favicon upload failed:', err);

      let errorMessage = 'Failed to upload favicon';
      if (err.status === 500) {
        errorMessage = 'Internal server error while uploading favicon. Please try again.';
      } else if (err.status === 415) {
        errorMessage = 'Unsupported file type. Please use ICO, PNG, JPG, SVG, or WebP format.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setUploadProgress(0);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFavicon = () => {
    setPreviewUrl(null);
    onFaviconUploaded('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg border border-gray-100 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
              <Star size={20} className="text-amber-500" />
              <span>Upload Favicon</span>
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Set a favicon for your website (appears in browser tabs)
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
            disabled={isUploading}
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('upload')}
            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'upload'
                ? 'text-amber-600 border-b-2 border-amber-600 bg-amber-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Upload size={16} />
            <span>Upload New</span>
          </button>
          <button
            onClick={() => setActiveTab('library')}
            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'library'
                ? 'text-amber-600 border-b-2 border-amber-600 bg-amber-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <FolderOpen size={16} />
            <span>Choose from Library</span>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 flex-1 overflow-y-auto">
          {/* Upload Tab */}
          {activeTab === 'upload' && (
            <>
              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <X size={20} className="text-red-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Upload Error</h3>
                      <div className="mt-2 text-sm text-red-700">
                        <pre className="whitespace-pre-wrap font-sans">{error}</pre>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Current/Preview Favicon */}
              {previewUrl && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Favicon
                  </label>
                  <div className="flex items-center space-x-4">
                    <div className="relative inline-block">
                      <div className="w-16 h-16 rounded-lg border-2 border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden">
                        <img
                          src={previewUrl}
                          alt="Favicon preview"
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                      <button
                        onClick={handleRemoveFavicon}
                        className="absolute -top-1 -right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                        title="Remove favicon"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                    {/* Browser tab preview */}
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-2">Browser tab preview:</p>
                      <div className="inline-flex items-center space-x-2 bg-gray-100 rounded-t-lg px-3 py-2 border border-gray-300 border-b-0">
                        <img
                          src={previewUrl}
                          alt="Tab favicon"
                          className="w-4 h-4 object-contain"
                        />
                        <span className="text-xs text-gray-700 truncate max-w-[120px]">
                          Your Website
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Upload Area */}
              <div className="space-y-4">
                <label className="block text-sm font-medium text-gray-700">
                  {previewUrl ? 'Change Favicon' : 'Upload Favicon'}
                </label>

                <div
                  className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    isDragging
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-gray-300 hover:border-gray-400'
                  } ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".ico,.png,.jpg,.jpeg,.svg,.webp,image/x-icon,image/vnd.microsoft.icon,image/png,image/jpeg,image/svg+xml,image/webp"
                    onChange={handleFileInputChange}
                    className="hidden"
                  />

                  {isUploading ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
                      </div>
                      <div className="space-y-2">
                        <p className="text-sm text-gray-600">Uploading favicon...</p>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-amber-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500">{uploadProgress}% complete</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center justify-center">
                        <div className="p-3 bg-amber-100 rounded-full">
                          <Image size={24} className="text-amber-600" />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <p className="text-sm text-gray-600">
                          Drop a favicon here, or{' '}
                          <button
                            onClick={handleBrowseClick}
                            className="text-amber-600 hover:text-amber-700 font-medium"
                          >
                            browse
                          </button>
                        </p>
                        <p className="text-xs text-gray-500">
                          ICO, PNG, JPG, SVG, or WebP (max 500KB)
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Tips */}
              <div className="mt-6 p-4 bg-amber-50 rounded-lg">
                <h4 className="text-sm font-medium text-amber-900 mb-2">Tips for best results:</h4>
                <ul className="text-sm text-amber-800 space-y-1">
                  <li>• Recommended size: 32x32 or 64x64 pixels</li>
                  <li>• Use PNG format for best quality</li>
                  <li>• ICO format supports multiple sizes</li>
                  <li>• Keep the design simple for small display</li>
                  <li>• Use a square image for best results</li>
                </ul>
              </div>
            </>
          )}

          {/* Library Tab */}
          {activeTab === 'library' && (
            <div className="space-y-4">
              {/* Search */}
              <div className="relative">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search images..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-amber-500 focus:border-amber-500"
                />
              </div>

              {/* Library Content */}
              {isLoadingLibrary ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 size={32} className="text-amber-600 animate-spin mb-4" />
                  <p className="text-gray-500">Loading media library...</p>
                </div>
              ) : libraryError ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <p className="text-red-500 mb-2">{libraryError}</p>
                  <button
                    onClick={() => setActiveTab('library')}
                    className="text-amber-600 hover:text-amber-700 text-sm"
                  >
                    Try again
                  </button>
                </div>
              ) : filteredLibraryMedia.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <FolderOpen size={48} className="text-gray-300 mb-4" />
                  <p className="text-gray-700 font-medium mb-2">No images found</p>
                  <p className="text-gray-500 text-sm">
                    {searchQuery
                      ? 'Try a different search term'
                      : 'Upload some images to get started'}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  {filteredLibraryMedia.map(item => {
                    const selected = selectedLibraryMedia?.id === item.id;
                    return (
                      <div
                        key={item.id}
                        onClick={() => setSelectedLibraryMedia(item)}
                        className={`group relative aspect-square rounded-lg border-2 overflow-hidden cursor-pointer transition-all ${
                          selected
                            ? 'border-amber-500 ring-2 ring-amber-200'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <img
                          src={item.cloudfrontUrl || item.url}
                          alt={item.filename}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        {/* Selection indicator */}
                        {selected && (
                          <div className="absolute inset-0 bg-amber-500/20 flex items-center justify-center">
                            <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center">
                              <Check size={18} className="text-white" />
                            </div>
                          </div>
                        )}
                        {/* Type badge */}
                        <div className="absolute top-1 left-1">
                          <Image size={12} className="text-white drop-shadow-lg" />
                        </div>
                        {/* Filename tooltip on hover */}
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <p className="text-xs text-white truncate">{item.filename}</p>
                          <p className="text-xs text-white/70">{formatFileSize(item.size)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-100">
          <button onClick={onClose} className="btn-secondary" disabled={isUploading}>
            Cancel
          </button>
          {activeTab === 'library' && (
            <button
              onClick={handleSelectFromLibrary}
              disabled={!selectedLibraryMedia}
              className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Use Selected
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
